import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Toolbar,
  Chip,
} from '@mui/material';
import testService from '../services/testService';

const TestLeaderboard = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState({ TestId: null, TestName: '', Total: 0, Top: [], Me: null });
  const [questionCount, setQuestionCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    const fetchLB = async () => {
      try {
        setLoading(true);
        const [lb, qs] = await Promise.all([
          testService.getLeaderboard(testId, true),
          testService.getQuestionsByTest(testId).catch(() => [])
        ]);
        if (mounted) {
          setData(lb || { TestId: testId, TestName: '', Total: 0, Top: [], Me: null });
          const qlen = Array.isArray(qs) ? qs.length : (qs?.length || 0);
          setQuestionCount(qlen);
        }
      } catch (e) {
        if (mounted) setError('Failed to load leaderboard');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchLB();
    return () => { mounted = false; };
  }, [testId]);

  const me = useMemo(() => data.Me || data.me || null, [data]);
  const rows = useMemo(() => (data.Top || data.top || []).map((r, idx) => {
    const correct = r.CorrectCount ?? r.correctCount ?? 0;
    const q = questionCount || 0;
    return {
      rank: r.Rank ?? r.rank ?? (idx + 1),
      studentId: r.StudentId ?? r.studentId,
      studentName: r.StudentName ?? r.studentName,
      correct,
      incorrect: Math.max(0, q - correct),
    };
  }), [data, questionCount]);

  const myIncorrect = useMemo(() => {
    if (!me) return 0;
    const corr = me.CorrectCount ?? me.correctCount ?? 0;
    const q = questionCount || 0;
    return Math.max(0, q - corr);
  }, [me, questionCount]);

  const myStudentId = useMemo(() => {
    const pk = localStorage.getItem('student_pk') || localStorage.getItem('student_id');
    return pk ? parseInt(pk, 10) : null;
  }, []);

  const maskSurname = (full) => {
    const parts = (full || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    const first = parts[0];
    const surname = parts.length > 1 ? parts[parts.length - 1] : '';
    if (!surname) return first;
    const visible = surname.slice(0, 3);
    const stars = '*'.repeat(Math.max(1, surname.length - 3));
    return `${first} ${visible}${stars}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>Leaderboard</Typography>
          <Typography variant="subtitle1" color="text.secondary">{data.TestName}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
          <Button variant="contained" onClick={() => navigate('/dashboard')}>Dashboard</Button>
        </Box>
      </Toolbar>

      <Paper sx={{ p: 2, mb: 2 }}>
        {me ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body1"><strong>Your rank:</strong> #{me.Rank ?? me.rank} of {data.Total ?? data.total}</Typography>
            <Chip color="success" label={`Correct: ${me.CorrectCount ?? me.correctCount ?? 0}`} />
            <Chip color="default" label={`Incorrect: ${myIncorrect}`} />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">Your rank will appear after you complete this test.</Typography>
        )}
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Rank</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="right">Correct</TableCell>
              <TableCell align="right">Incorrect</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => {
              const isMe = myStudentId && row.studentId === myStudentId;
              return (
                <TableRow key={idx} selected={isMe} sx={isMe ? { backgroundColor: 'rgba(25,118,210,0.08)' } : undefined}>
                  <TableCell>#{row.rank}</TableCell>
                  <TableCell>{maskSurname(row.studentName)}</TableCell>
                  <TableCell align="right">{row.correct}</TableCell>
                  <TableCell align="right">{row.incorrect}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TestLeaderboard;
